import { FieldShell } from '@today/ui-components'
import { Input, textareaVariants } from '@today/ui-primitives'
import { type ReactElement, type RefObject, useEffect } from 'react'

/**
 * Field helpers of the content organisms. Text/select/error helpers are shared
 * with the product organisms; these add the date-time input, a native checkbox
 * and the dialog focus helper.
 */
export { ErrorText, SelectInput, TextArea, TextInput } from '../product/product-fields'

/** Move focus to `ref` once a dialog has opened (after the dialog focused its own panel). */
export function useFocusOnOpen(ref: RefObject<HTMLElement | null>, isOpen: boolean): void {
	useEffect(() => {
		if (!isOpen) return
		const timer = setTimeout(() => ref.current?.focus(), 0)
		return () => clearTimeout(timer)
	}, [isOpen, ref])
}

/** A labelled multi-line input whose element can take focus programmatically (native, styled as `Textarea`). */
export function FocusTextArea(props: {
	label: string
	value: string
	error?: string
	inputRef: RefObject<HTMLTextAreaElement | null>
	onChange: (value: string) => void
}): ReactElement {
	return (
		<FieldShell label={props.label} error={props.error}>
			<textarea
				ref={props.inputRef}
				className={textareaVariants()}
				value={props.value}
				onChange={(e) => props.onChange(e.target.value)}
			/>
		</FieldShell>
	)
}

/** A labelled `datetime-local` input (the value is read as UTC by the page hook). */
export function DateTimeInput(props: {
	label: string
	value: string
	error?: string
	description?: string
	onChange: (value: string) => void
}): ReactElement {
	return (
		<FieldShell label={props.label} error={props.error} description={props.description}>
			<Input type="datetime-local" value={props.value} onChange={(e) => props.onChange(e.target.value)} />
		</FieldShell>
	)
}

/** A native checkbox with its label. */
export function CheckboxInput(props: {
	label: string
	checked: boolean
	disabled?: boolean
	onChange: (checked: boolean) => void
}): ReactElement {
	return (
		<label className="flex items-center gap-2 text-ds-sm text-ds-text">
			<input
				type="checkbox"
				checked={props.checked}
				disabled={props.disabled}
				onChange={(e) => props.onChange(e.target.checked)}
			/>
			{props.label}
		</label>
	)
}
